import React, { useEffect, useState, useMemo } from 'react';
import api, { spacesAPI, communityAPI } from '../../services/api.js';
import { Globe, Lock, Crown, Search, Key, User, Loader2, Plus, Layers, MoreVertical, ArrowUpRight, Settings, Trash2, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { getUserIdFromToken } from '../../utils/auth.js';
import {
  EntityCardShell, EntityCardHeader, EntityCardDescription, EntityCardMeta,
  EntityCardMetrics, EntityCardFooter, EntityPrimaryButton, EntityIconButton,
} from '../../components/ui/EntityCard.jsx';

// ─── Design tokens — align with Agents page palette ───────────────────────
const T = {
  page:        'var(--lyra-color-surface)',
  surface:     'var(--lyra-color-surface)',
  surface2:    'var(--lyra-color-surface-2)',
  surface3:    'var(--app-surface-3)',
  border:      'var(--lyra-color-border)',
  border2:     'var(--app-border-strong)',
  ink:         'var(--lyra-color-text)',
  ink2:        'var(--app-text-2)',
  ink3:        'var(--lyra-color-text-muted)',
  ink4:        'var(--app-text-3)',
  accent:      'var(--lyra-color-primary)',
  accentLight: 'var(--app-primary-soft)',
  accentMid:   'var(--app-primary-soft-2)',
  accentDark:  'var(--app-primary-deep)',
  green:       'var(--lyra-color-success)',
  greenBg:     'var(--lyra-color-success-bg)',
  greenBd:     'var(--app-success-border)',
  greenText:   '#14532D',
  violet:      'var(--lyra-color-primary)',
  violetBg:    'var(--app-primary-soft)',
  violetBd:    'var(--app-primary-soft-2)',
  violetText:  'var(--app-primary-deep)',
  red:         'var(--lyra-color-danger)',
  redBg:       'var(--lyra-color-danger-bg)',
  redBd:       'var(--app-danger-border)',
  amber:       'var(--lyra-color-warning)',
  amberBg:     'var(--lyra-color-warning-bg)',
  amberBd:     'var(--app-warning-border)',
  // Backwards-compatible aliases used across SpacesHub
  greenHover: '#145c44',
  greenLight: 'var(--app-primary-soft)',
  tealMid: 'var(--app-primary-soft-2)',
  tealText: 'var(--app-primary-deep)',
  purpleLight: 'var(--app-primary-soft)',
  purpleText: 'var(--app-primary-deep)',
  purpleBorder: 'var(--app-primary-soft-2)',
  amberLight: 'var(--lyra-color-warning-bg)',
  amberText: 'var(--lyra-color-warning)',
  amberBorder: 'var(--app-warning-border)',
  coralLight: 'var(--lyra-color-danger-bg)',
  coralText: '#993c1d',
  redLight: 'var(--lyra-color-danger-bg)',
  redText: 'var(--lyra-color-danger)',
  redBorder: 'var(--app-danger-border)',
  bg: 'var(--lyra-color-surface)',
  surfaceAlt: 'var(--lyra-color-surface-2)',
  borderStrong: 'var(--app-border-strong)',
  text: 'var(--lyra-color-text)',
  textMid: 'var(--app-text-2)',
  muted: 'var(--lyra-color-text-muted)',
  mutedLight: 'var(--app-text-3)',
};

// ─── Avatar color palette — deterministic from _id ───────────────────────────
const AVATAR_PALETTE = [
  { bg: 'var(--app-primary-soft)', fg: 'var(--lyra-color-success)' },
  { bg: 'var(--app-primary-soft)', fg: 'var(--lyra-color-primary)' },
  { bg: 'var(--lyra-color-warning-bg)', fg: '#854f0b' },
  { bg: 'var(--lyra-color-danger-bg)', fg: '#993c1d' },
  { bg: 'var(--app-primary-soft)', fg: 'var(--app-primary-deep)' },
  { bg: 'var(--app-primary-soft)', fg: 'var(--lyra-color-success)' },
  { bg: 'var(--app-surface-3)', fg: '#993556' },
];
function avatarColor(id: string) {
  const idx = parseInt((id || '0').slice(-2), 16) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[idx];
}
function initials(name: string) {
  return (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeIn  { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 4px; }
  .agent-highlight { animation: agent-pulse 2s ease-out; }
  @keyframes agent-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(127,119,221,0.45); }
    30%  { box-shadow: 0 0 0 10px rgba(127,119,221,0.12); }
    100% { box-shadow: 0 0 0 0 rgba(127,119,221,0); }
  }
`;

// ─── Icon button ─────────────────────────────────────────────────────────────
function IconBtn({
  children, onClick, danger = false, title = '', disabled = false,
}: {
  children: React.ReactNode; onClick?: () => void;
  danger?: boolean; title?: string; disabled?: boolean;
}) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 30, height: 30, borderRadius: 8, border: `0.5px solid ${hov && danger ? T.redBorder : T.border}`,
        background: hov ? (danger ? T.redLight : T.surfaceAlt) : T.surface,
        color: hov && danger ? T.redText : T.muted,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer', flexShrink: 0,
        transition: 'all 0.12s ease', fontSize: 14,
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {children}
    </button>
  );
}

// ─── Pill button ─────────────────────────────────────────────────────────────
function PillBtn({
  children, onClick, variant = 'outline', disabled = false, loading = false, style: extraStyle = {},
}: {
  children: React.ReactNode; onClick?: () => void;
  variant?: 'solid' | 'outline' | 'ghost' | 'teal' | 'purple' | 'red' | 'amber';
  disabled?: boolean; loading?: boolean; style?: React.CSSProperties;
}) {
  const [hov, setHov] = useState(false);
  const variants: Record<string, React.CSSProperties> = {
    solid:   { background: hov ? T.accentDark : T.accent, color: 'var(--app-on-accent)', border: 'none' },
    outline: { background: hov ? T.surface2 : T.surface, color: T.ink2, border: `1px solid ${T.border}` },
    ghost:   { background: hov ? T.surface2 : 'transparent', color: T.ink3, border: `1px solid ${T.border}` },
    teal:    { background: hov ? T.accentLight : T.accentLight, color: T.accentDark, border: `1px solid ${T.accentMid}` },
    purple:  { background: hov ? T.violetBg : T.violetBg, color: T.violetText, border: `1px solid ${T.violetBd}` },
    red:     { background: hov ? T.redBg : T.redBg, color: T.red, border: `1px solid ${T.redBd}` },
    amber:   { background: T.amberBg, color: T.amber, border: `1px solid ${T.amberBd}` },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
        padding: '6px 13px', borderRadius: 20, fontSize: 11, fontWeight: 500,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled && !loading ? 0.5 : 1,
        transition: 'all 0.12s ease', whiteSpace: 'nowrap',
        ...variants[variant],
        ...extraStyle,
      }}
    >
      {loading ? 'Loading…' : children}
    </button>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({
  label, sub, icon, children, action,
}: {
  label: string; sub?: string; icon?: string;
  children: React.ReactNode; action?: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          {icon && <span style={{ fontSize: 14, opacity: 0.7 }}>{icon}</span>}
          <span style={{ fontSize: 12, fontWeight: 500, color: T.textMid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {label}
          </span>
          {sub && (
            <span style={{ fontSize: 11, color: T.mutedLight, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
              · {sub}
            </span>
          )}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function Empty({ msg }) {
  return (
    <div style={{
      padding: '20px 16px', borderRadius: 10, border: `0.5px dashed ${T.border}`,
      background: T.surfaceAlt, color: T.mutedLight, fontSize: 12, textAlign: 'center',
    }}>
      {msg}
    </div>
  );
}

// ─── Owned space card ─────────────────────────────────────────────────────────
function OwnedSpaceCard({ space, onInvite, onSettings, onDelete, onVisibilityChange }) {
  const navigate = useNavigate();
  const [visLoading, setVisLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const agentCount = (space.members || []).filter(m => m.lyranAgentId).length;
  // Spaces don't track workflow runs anywhere in this app today — shown as 0
  // rather than fabricating a number, matching the reference design's stat row.
  const workflowCount = 0;

  async function toggleVisibility() {
    if (visLoading) return;
    const next = space.visibility === 'public' ? 'private' : 'public';
    setVisLoading(true);
    try {
      const r = await spacesAPI.update(space._id, { visibility: next });
      const updated = r.space || r;
      if (onVisibilityChange) onVisibilityChange({ ...updated, isPublic: updated.visibility === 'public' });
      toast.success(`Visibility set to ${next}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update visibility');
    } finally {
      setVisLoading(false);
      setMenuOpen(false);
    }
  }

  const menu = (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setMenuOpen(v => !v)} title="Space actions" aria-label="Space actions"
        style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, color: T.ink3, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
        <MoreVertical size={16} />
      </button>
      {menuOpen && (
        <>
          <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 10 }} />
          <div style={{ position: 'absolute', top: 38, right: 0, zIndex: 11, minWidth: 180, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.12)', padding: 6 }}>
            <button onClick={() => { setMenuOpen(false); onInvite(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', border: 'none', background: 'none', color: T.text, fontSize: 12.5, fontWeight: 500, padding: '8px 10px', borderRadius: 7, cursor: 'pointer', textAlign: 'left' }}>
              <Plus size={14} /> Invite Lyrans
            </button>
            <button onClick={toggleVisibility} disabled={visLoading}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', border: 'none', background: 'none', color: T.text, fontSize: 12.5, fontWeight: 500, padding: '8px 10px', borderRadius: 7, cursor: visLoading ? 'not-allowed' : 'pointer', textAlign: 'left' }}>
              {visLoading ? <Loader2 size={14} /> : (space.visibility === 'public' ? <Lock size={14} /> : <Globe size={14} />)}
              {space.visibility === 'public' ? 'Make private' : 'Make public'}
            </button>
            <button onClick={() => { setMenuOpen(false); onDelete(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', border: 'none', background: 'none', color: T.red, fontSize: 12.5, fontWeight: 500, padding: '8px 10px', borderRadius: 7, cursor: 'pointer', textAlign: 'left' }}>
              <Trash2 size={14} /> Delete space
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <EntityCardShell
      onClick={() => navigate(`/spaces/${space._id}`)}
    >
      <EntityCardHeader icon={Layers} eyebrow="Space" title={space.name} status={space.visibility === 'public' ? 'Public' : 'Private'} statusTone="accent" menu={menu} />
      <EntityCardDescription>
        {space.description || 'Personal workspace for ideas, experiments and day-to-day work.'}
      </EntityCardDescription>
      <EntityCardMeta icon={Crown}>Owner</EntityCardMeta>
      <EntityCardMetrics items={[
        { value: space.memberCount || 0, label: 'Members' },
        { value: agentCount, label: agentCount === 1 ? 'Agent' : 'Agents' },
        { value: workflowCount, label: 'Workflows' },
      ]} />
      <EntityCardFooter>
        <EntityPrimaryButton onClick={e => { e.stopPropagation(); navigate(`/spaces/${space._id}`); }}><ArrowUpRight size={15} /> Open space</EntityPrimaryButton>
        <EntityIconButton onClick={e => { e.stopPropagation(); onSettings(); }} title="Space settings"><Settings size={16} /></EntityIconButton>
      </EntityCardFooter>
    </EntityCardShell>
  );
}

// ─── Discover space card ──────────────────────────────────────────────────────
function DiscoverCard({ space, onJoin, onRequest, onOpen, onLeave }) {
  const navigate = useNavigate();
  const [joining, setJoining] = useState(false);
  const agentCount = (space.members || []).filter(m => m.lyranAgentId).length;

  const cta = () => {
    if (space.isMember) return 'open';
    if (space.isRequested) return 'requested';
    if (space.isInvited) return 'invited';
    if (space.visibility === 'public') return 'join';
    return 'request';
  };

  async function handleJoin() {
    if (joining) return;
    setJoining(true);
    try { await onJoin(); } finally { setJoining(false); }
  }

  const action = cta();
  const actionLabel = {
    open: 'Open space', join: 'Join space', request: 'Request to join',
    requested: 'Request pending', invited: 'Accept invite',
  }[action];
  const runAction = (event) => {
    event.stopPropagation();
    if (action === 'open') onOpen();
    else if (action === 'join') handleJoin();
    else if (action === 'request') onRequest();
    else if (action === 'invited') onJoin();
  };

  return (
    <EntityCardShell
      onClick={() => navigate(`/spaces/${space._id}`)}
    >
      <EntityCardHeader icon={Layers} eyebrow="Space" title={space.name} status={space.visibility === 'public' ? 'Public' : 'Private'} statusTone="accent" />
      <EntityCardDescription>
        {space.description || 'A shared workspace for focused collaboration between people and AI agents.'}
      </EntityCardDescription>
      <EntityCardMeta icon={space.visibility === 'public' ? Globe : Lock}>
        {space.visibility === 'public' ? 'Discoverable team space' : 'Private team space'}
      </EntityCardMeta>
      <EntityCardMetrics items={[
        { value: space.memberCount || 0, label: 'Members' },
        { value: agentCount, label: agentCount === 1 ? 'Agent' : 'Agents' },
        { value: 0, label: 'Workflows' },
      ]} />
      <EntityCardFooter>
        <EntityPrimaryButton onClick={runAction} disabled={action === 'requested' || joining}>
          {joining ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <ArrowUpRight size={15} />}
          {joining ? 'Joining…' : actionLabel}
        </EntityPrimaryButton>
        {space.isMember && (
          <EntityIconButton onClick={e => { e.stopPropagation(); onLeave(); }} title="Leave space"><LogOut size={16} /></EntityIconButton>
        )}
      </EntityCardFooter>
    </EntityCardShell>
  );
}

// ─── Lyran row ────────────────────────────────────────────────────────────────
function LyranRow({ lyran }) {
  const col = avatarColor(lyran._id || '');
  const [following, setFollowing] = useState(lyran.isFollowing || false);
  const [followLoading, setFollowLoading] = useState(false);
  const [showMcp, setShowMcp] = useState(false);
  const [mcpLoading, setMcpLoading] = useState(false);
  const [mcpData, setMcpData] = useState(null);
  const [mcpRevealed, setMcpRevealed] = useState(false);
  const [avgRating, setAvgRating] = useState(lyran.avgRating || 0);
  const [ratingCount, setRatingCount] = useState(lyran.ratingCount || 0);
  const [myRating, setMyRating] = useState(lyran.myRating || 0);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [hoverStar, setHoverStar] = useState(0);
  const navigate = useNavigate();

  async function rate(value) {
    if (ratingLoading || value === myRating) return;
    const prevAvg = avgRating, prevCount = ratingCount, prevMine = myRating;
    setRatingLoading(true);
    setMyRating(value);
    try {
      const userId = lyran.userId || lyran._id;
      const res = await communityAPI.rateLyran(userId, value);
      setAvgRating(res.avgRating || 0);
      setRatingCount(res.ratingCount || 0);
    } catch (err) {
      setAvgRating(prevAvg);
      setRatingCount(prevCount);
      setMyRating(prevMine);
      toast.error(err?.response?.data?.error || 'Failed to submit rating');
    } finally {
      setRatingLoading(false);
    }
  }

  async function toggleFollow() {
    if (followLoading) return;
    setFollowLoading(true);
    const next = !following;
    setFollowing(next);
    try {
      const userId = lyran.userId || lyran._id;
      // Use communityAPI toggle which posts to /api/community/follow/:id
      await communityAPI.toggleFollow(userId);
    } catch {
      setFollowing(!next);
      toast.error('Failed to update follow');
    } finally {
      setFollowLoading(false);
    }
  }

  async function toggleMcp() {
    if (showMcp) { setShowMcp(false); return; }
    if (!lyran.mcpKeyPublished) { toast.error('No MCP key published'); return; }
    setMcpLoading(true);
    try {
      const r = await api.get(`/lyrans/${lyran._id}/mcp-key`).then(r => r.data);
      setMcpData(r || null);
      setShowMcp(true);
    } catch {
      toast.error('No MCP key published');
    } finally {
      setMcpLoading(false);
    }
  }

  const displayName = lyran.lyran_name || lyran.name || 'Unknown';
  const handle = lyran.lyran_name || lyran.name || '';

  return (
    <div style={{
      background: T.surface,
      border: `0.5px solid ${T.border}`,
      borderRadius: 10,
      padding: '11px 14px',
      display: 'flex', alignItems: 'center', gap: 12,
      transition: 'box-shadow 0.12s ease',
      position: 'relative',
    }}
      onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.05)')}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
    >
      {/* Avatar with online ring */}
      <div style={{ position: 'relative', flexShrink: 0, cursor: 'pointer'   }}
      onClick={() => navigate(`/teams/${lyran.userId}`)}
      >
        {lyran.avatar ? (
          <img
            src={lyran.avatar}
            alt={displayName}
            style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
            onError={e => { const t = e.target; if (t && t.style) t.style.display = 'none'; }}
          />
        ) : (
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: col.bg, color: col.fg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 500,
          }}>
            {initials(lyran.name || displayName)}
          </div>
        )}
        {/* Online dot */}
        <span style={{
          position: 'absolute', bottom: 0, right: 0,
          width: 9, height: 9, borderRadius: '50%',
          background: lyran.isOnline ? T.tealMid : T.mutedLight,
          border: `2px solid ${T.surface}`,
        }} aria-label={lyran.isOnline ? 'Online' : 'Offline'} />
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{displayName}</div>
        <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
          @{handle.toLowerCase().replace(/\s+/g, '_')}
          {lyran.bio && <span style={{ color: T.mutedLight }}> · {lyran.bio.slice(0, 40)}{lyran.bio.length > 40 ? '…' : ''}</span>}
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, marginRight: 8, flexShrink: 0 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{lyran.publicAgentCount || 0}</div>
          <div style={{ fontSize: 10, color: T.mutedLight }}>agents</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{(lyran.followers || []).length}</div>
          <div style={{ fontSize: 10, color: T.mutedLight }}>followers</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{ratingCount > 0 ? `⭐ ${avgRating}` : '—'}</div>
          <div style={{ fontSize: 10, color: T.mutedLight }}>{ratingCount > 0 ? `${ratingCount} rating${ratingCount > 1 ? 's' : ''}` : 'no ratings'}</div>
        </div>
        {lyran.streak?.current > 0 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: T.amberText }}>🔥 {lyran.streak.current}</div>
            <div style={{ fontSize: 10, color: T.mutedLight }}>streak</div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
        <div
          style={{ display: 'inline-flex', alignItems: 'center', gap: 1, opacity: ratingLoading ? 0.6 : 1, cursor: ratingLoading ? 'default' : 'pointer' }}
          onMouseLeave={() => setHoverStar(0)}
          title={myRating ? `You rated ${myRating}/5` : 'Rate this Lyran'}
        >
          {[1, 2, 3, 4, 5].map(n => (
            <span
              key={n}
              onMouseEnter={() => setHoverStar(n)}
              onClick={() => rate(n)}
              style={{ fontSize: 14, color: (hoverStar || myRating) >= n ? 'var(--lyra-color-warning)' : T.mutedLight, lineHeight: 1 }}
            >★</span>
          ))}
        </div>
        <PillBtn
          variant={following ? 'teal' : 'outline'}
          onClick={toggleFollow}
          loading={followLoading}
        >
          {following ? '✓ Following' : 'Follow'}
        </PillBtn>
        <div style={{ position: 'relative' }}>
          <PillBtn
            variant="purple"
            onClick={toggleMcp}
            loading={mcpLoading}
            disabled={!lyran.mcpKeyPublished}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Key size={12} /> MCP key</span>
          </PillBtn>

          {showMcp && mcpData && (
            <div style={{
              position: 'absolute', right: 0, top: 'calc(100% + 6px)',
              background: T.surface, border: `0.5px solid ${T.borderStrong}`,
              borderRadius: 10, padding: 14, width: 300, zIndex: 50,
              boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: T.text, marginBottom: 10 }}>
                {displayName}'s MCP endpoint
              </div>
              <div style={{ fontSize: 11, color: T.muted, marginBottom: 4 }}>Endpoint</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <div style={{
                  flex: 1, fontFamily: 'monospace', fontSize: 11,
                  background: T.surfaceAlt, padding: '6px 8px', borderRadius: 6,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  color: T.textMid,
                }}>
                  {mcpData.endpoint || '—'}
                </div>
                <IconBtn onClick={() => { navigator.clipboard.writeText(mcpData.endpoint || ''); toast.success('Copied!'); }} title="Copy endpoint">📋</IconBtn>
              </div>
              <div style={{ fontSize: 11, color: T.muted, marginBottom: 4 }}>API key</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  flex: 1, fontFamily: 'monospace', fontSize: 11,
                  background: T.surfaceAlt, padding: '6px 8px', borderRadius: 6,
                  color: T.textMid, letterSpacing: mcpRevealed ? 0 : 2,
                }}>
                  {mcpData.key
                    ? (mcpRevealed ? mcpData.key : mcpData.key.replace(/.(?=.{4})/g, '•'))
                    : '—'
                  }
                </div>
                <IconBtn onClick={() => setMcpRevealed(r => !r)} title={mcpRevealed ? 'Hide' : 'Reveal'}>
                  {mcpRevealed ? '🙈' : '👁'}
                </IconBtn>
                <IconBtn onClick={() => { navigator.clipboard.writeText(mcpData.key || ''); toast.success('Copied!'); }} title="Copy key" disabled={!mcpData.key}>📋</IconBtn>
              </div>
              <button onClick={() => setShowMcp(false)} style={{
                marginTop: 10, width: '100%', padding: '6px 0', borderRadius: 6,
                border: `0.5px solid ${T.border}`, background: T.surfaceAlt,
                fontSize: 11, color: T.muted, cursor: 'pointer',
              }}>Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tab component ────────────────────────────────────────────────────────────
function Tab({
  label, count, active, onClick, icon,
}: {
  label: string; count: number; active: boolean; onClick: () => void; icon: string;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '7px 14px', borderRadius: active ? 10 : 20, border: 'none',
        background: active ? T.accent : 'transparent',
        color: active ? 'var(--lyra-color-surface)' : T.ink3,
        fontSize: 13, fontWeight: active ? 600 : 500,
        cursor: 'pointer', transition: 'all 0.12s ease',
      }}
    >
      <span style={{ fontSize: 14 }}>{icon}</span>
      {label}
      <span style={{
        fontSize: 11, padding: '2px 7px', borderRadius: 10,
        background: active ? 'rgba(255,255,255,0.14)' : T.surface2,
        color: active ? 'var(--lyra-color-surface)' : T.ink4,
      }}>
        {count}
      </span>
    </button>
  );
}

// ─── Create space modal ───────────────────────────────────────────────────────
function CreateSpaceModal({
  onClose, onCreate,
}: {
  onClose: () => void;
  onCreate: (name: string, visibility: string) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [vis, setVis] = useState('public');
  const [loading, setLoading] = useState(false);

  async function submit() {
    if (!name.trim() || loading) return;
    setLoading(true);
    try { await onCreate(name.trim(), vis); } finally { setLoading(false); }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.35)', zIndex: 100, backdropFilter: 'blur(2px)',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: 440, background: T.surface, borderRadius: 14,
        border: `0.5px solid ${T.borderStrong}`,
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        overflow: 'hidden',
      }}>
        {/* Modal header */}
        <div style={{
          padding: '16px 20px 14px',
          borderBottom: `0.5px solid ${T.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: T.text }}>Create a new space</div>
          <IconBtn onClick={onClose} title="Close">✕</IconBtn>
        </div>

        <div style={{ padding: '18px 20px' }}>
          {/* Name */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: T.muted, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Space name
            </label>
            <input
              autoFocus
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && submit()}
              placeholder="e.g. Founders' Space"
              style={{
                width: '100%', padding: '9px 12px', borderRadius: 8,
                border: `0.5px solid ${T.borderStrong}`, fontSize: 13,
                color: T.text, outline: 'none',
                background: T.surface,
              }}
            />
          </div>

          {/* Visibility */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 500, color: T.muted, display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Visibility
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['public', 'private'].map(v => (
                <button
                  key={v}
                  onClick={() => setVis(v)}
                  style={{
                    flex: 1, padding: '9px 12px', borderRadius: 8, cursor: 'pointer',
                    border: vis === v
                      ? `1.5px solid ${v === 'public' ? T.tealMid : T.purpleBorder}`
                      : `0.5px solid ${T.border}`,
                    background: vis === v
                      ? (v === 'public' ? T.greenLight : T.purpleLight)
                      : T.surface,
                    color: vis === v
                      ? (v === 'public' ? T.tealText : T.purpleText)
                      : T.muted,
                    fontSize: 12, fontWeight: vis === v ? 500 : 400,
                    transition: 'all 0.12s ease',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {v === 'public' ? <Globe size={14} /> : <Lock size={14} />}
                    {v === 'public' ? 'Public' : 'Private'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px 16px',
          borderTop: `0.5px solid ${T.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <PillBtn variant="ghost" onClick={onClose}>Cancel</PillBtn>
          <PillBtn variant="solid" onClick={submit} loading={loading} disabled={!name.trim()}>
            Create space
          </PillBtn>
        </div>
      </div>
    </div>
  );
}

// ─── Invite Lyrans modal ──────────────────────────────────────────────────────
function InviteModal({ space, lyrans, onClose, onSend }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const memberIds = new Set((space.members || []).map(m => m.userId));

  const filtered = lyrans.filter(l => {
    if (memberIds.has(l.userId)) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return (l.name || '').toLowerCase().includes(q) || (l.lyran_name || '').toLowerCase().includes(q);
  });

  async function submit() {
    if (!selected.length || loading) return;
    setLoading(true);
    try { await onSend(selected); } finally { setLoading(false); }
  }

  function toggle(id) {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.35)', zIndex: 100, backdropFilter: 'blur(2px)',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: 560, maxHeight: '80vh', background: T.surface, borderRadius: 14,
        border: `0.5px solid ${T.borderStrong}`,
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px 14px', borderBottom: `0.5px solid ${T.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: T.text }}>Invite Lyrans</div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{space.name}</div>
          </div>
          <IconBtn onClick={onClose} title="Close">✕</IconBtn>
        </div>

        {/* Search */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 9, padding: "7px 12px", width: 240, marginBottom: 18 }}>
                    <Search size={13} color={T.ink4} />
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search Lyrans to invite…"
            style={{
              width: '100%', padding: '8px 12px', borderRadius: 8,
              border: `0.5px solid ${T.borderStrong}`, fontSize: 12, color: T.text, outline: 'none',
            }}
          />
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 20px' }}>
          {filtered.length === 0 && <Empty msg="No Lyrans available to invite" />}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {filtered.map(l => {
              const col = avatarColor(l.userId || '');
              const isSelected = selected.includes(l.userId);
              return (
                <div
                  key={l.userId}
                  onClick={() => toggle(l.userId)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 12px', borderRadius: 8, cursor: 'pointer',
                    border: `0.5px solid ${isSelected ? T.tealMid : T.border}`,
                    background: isSelected ? T.greenLight : T.surface,
                    transition: 'all 0.1s ease',
                  }}
                >
                  <div style={{
                    width: 30, height: 30, borderRadius: '50%',
                    background: col.bg, color: col.fg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 500, flexShrink: 0,
                  }}>
                    {initials(l.name || l.lyran_name || '')}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.text }}>
                      {l.lyran_name || l.name}
                    </div>
                    <div style={{ fontSize: 11, color: T.muted }}>
                      @{(l.lyran_name || l.name || '').toLowerCase().replace(/\s+/g, '_')}
                    </div>
                  </div>
                  <div style={{
                    width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                    border: `1.5px solid ${isSelected ? T.tealMid : T.borderStrong}`,
                    background: isSelected ? T.green : T.surface,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--app-on-accent)', fontSize: 11, transition: 'all 0.1s ease',
                  }}>
                    {isSelected && '✓'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px 16px', borderTop: `0.5px solid ${T.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div style={{ fontSize: 11, color: T.muted }}>
            {selected.length > 0
              ? `${selected.length} Lyran${selected.length > 1 ? 's' : ''} selected`
              : 'Select Lyrans to invite'
            }
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <PillBtn variant="ghost" onClick={onClose}>Cancel</PillBtn>
            <PillBtn variant="solid" onClick={submit} loading={loading} disabled={!selected.length}>
              Send invites
            </PillBtn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function SpacesHub() {
  const FONT_IMPORT = "@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap')";
  const [spaces, setSpaces] = useState([]);
  const [publicSpaces, setPublicSpaces] = useState([]);
  const [lyrans, setLyrans] = useState([]);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteSpace, setInviteSpace] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const [spacesRes, publicRes, lyransRes] = await Promise.all([
        spacesAPI.list().catch(() => ({ spaces: [] })),
        spacesAPI.list({ filter: 'public', limit: 200 }).catch(() => ({ spaces: [] })),
        communityAPI.getMembers().catch(() => ({ members: [] })),
      ]);
      const annotate = arr => arr.map(s => ({ ...s, isPublic: s.visibility === 'public' }));
      setSpaces(annotate(spacesRes.spaces || spacesRes || []));
      setPublicSpaces(annotate(publicRes.spaces || publicRes || []));
      // support different response shapes: { members: [...] } or { lyrans: [...] } or plain array
      const membersList = lyransRes?.members || lyransRes?.lyrans || (Array.isArray(lyransRes) ? lyransRes : []);
      setLyrans(membersList || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load data');
    }
  }

  // ── Derived lists ────────────────────────────────────────────────────────────
  const ql = q.trim().toLowerCase();

  const ownedSpaces = useMemo(() =>
    spaces.filter(s => s.isOwner && (!ql || s.name?.toLowerCase().includes(ql))),
    [spaces, ql]);

  const memberSpaces = useMemo(() =>
    spaces.filter(s => s.isMember && !s.isOwner && (!ql || s.name?.toLowerCase().includes(ql))),
    [spaces, ql]);

  const discoverList = useMemo(() => {
    const pub = publicSpaces.filter(s =>
      !s.isOwner && (!ql || s.name?.toLowerCase().includes(ql)));
    const members = spaces.filter(s =>
      s.isMember && !s.isOwner && (!ql || s.name?.toLowerCase().includes(ql)));
      const map = new Map();
      pub.forEach(s => map.set(s._id, s));
      members.forEach(s => { if (!map.has(s._id)) map.set(s._id, s); });
    return Array.from(map.values());
  }, [publicSpaces, spaces, ql]);

  const filteredLyrans = useMemo(() =>
    lyrans.filter(l => {
      if (!ql) return true;
      return (l.name || '').toLowerCase().includes(ql)
        || (l.lyran_name || '').toLowerCase().includes(ql);
    }), [lyrans, ql]);

  const myCount = useMemo(() =>
    spaces.filter(s => s.isOwner || s.isMember).length, [spaces]);

  // ── Actions ──────────────────────────────────────────────────────────────────
  async function handleCreate(name: string, visibility: string) {
    const r = await spacesAPI.create({ name, visibility });
    const ns = r.space || r;
    if (ns) ns.isPublic = ns.visibility === 'public';
    setSpaces(prev => [ns, ...prev]);
    setShowNew(false);
    toast.success('Space created!');
  }

  // A space can appear in both `spaces` and `publicSpaces` (the Discover list prefers the public copy), so every
  // optimistic change has to be applied to both or the card keeps showing the old action.
  async function doJoin(space) {
    const prev = spaces;
    const prevPublic = publicSpaces;
    const joined = s => (s._id === space._id ? { ...s, isMember: true, isInvited: false, memberCount: (s.memberCount || 0) + 1 } : s);
    setSpaces(prev.map(joined));
    setPublicSpaces(prevPublic.map(joined));
    try {
      await spacesAPI.join(space._id);
      toast.success(`Joined ${space.name}`);
    } catch {
      setSpaces(prev);
      setPublicSpaces(prevPublic);
      toast.error('Failed to join');
    }
  }

  async function doRequest(space) {
    const prev = spaces;
    const prevPublic = publicSpaces;
    const requested = s => (s._id === space._id ? { ...s, isRequested: true } : s);
    setSpaces(prev.map(requested));
    setPublicSpaces(prevPublic.map(requested));
    try {
      await spacesAPI.requestJoin(space._id);
      toast.success('Request sent');
    } catch {
      setSpaces(prev);
      setPublicSpaces(prevPublic);
      toast.error('Failed to send request');
    }
  }

  async function doLeave(space) {
    if (!window.confirm(`Leave "${space.name}"?`)) return;
    const prevSpaces = spaces;
    const prevPublic = publicSpaces;

    // Optimistic update: mark as not a member in both local lists so UI updates immediately
    setSpaces(prevSpaces.map(s => s._id === space._id
      ? { ...s, isMember: false, memberCount: Math.max((s.memberCount || 1) - 1, 0) }
      : s));
    setPublicSpaces(prevPublic.map(p => p._id === space._id
      ? { ...p, isMember: false, memberCount: Math.max((p.memberCount || 1) - 1, 0) }
      : p));

    try {
      await spacesAPI.leave(space._id);
      toast.success('Left space');
    } catch {
      // Revert on error
      setSpaces(prevSpaces);
      setPublicSpaces(prevPublic);
      toast.error('Failed to leave');
    }
  }

  async function doDelete(space) {
    if (!window.confirm(`Delete "${space.name}"? This cannot be undone.`)) return;
    try {
      await spacesAPI.delete(space._id);
      setSpaces(prev => prev.filter(s => s._id !== space._id));
      toast.success('Space deleted');
    } catch {
      toast.error('Failed to delete');
    }
  }

  async function sendInvites(ids) {
    if (!inviteSpace) return;
    try {
      for (const id of ids) {
        await spacesAPI.invite(inviteSpace._id, id);
      }
      toast.success('Invitations sent!');
      setInviteOpen(false);
      setInviteSpace(null);
    } catch (err) {
      console.error(err);
      toast.error('Failed to send some invitations');
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
        <div style={{ minHeight: "100vh", background: T.page, fontFamily: "DM Sans, system-ui, sans-serif" }}>
      <style>{GLOBAL_CSS}</style>
      <div style={{
        maxWidth: 1280, margin: '0 auto', padding: '28px 32px 60px',
        fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        color: T.ink2,
      }}>
        {/* ── Header — matches the AI Team page layout ──────────────────── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Teams</div>
            <div style={{ fontSize: 13, color: T.ink3 }}>Discover teams and connect with Lyrans</div>
          </div>
          <button onClick={() => setShowNew(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 9, background: T.accent, color: 'var(--app-on-accent)', border: 'none', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 2px 8px undefined' }}>
            <Plus size={14} /> New space
          </button>
        </div>

        {/* ── Stat tiles ─────────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--app-primary-soft)', color: 'var(--lyra-color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Globe size={17} /></div>
            <div><div style={{ fontSize: 19, fontWeight: 700, color: T.ink }}>{spaces.length}</div><div style={{ fontSize: 11, color: T.ink4 }}>Spaces</div></div>
          </div>
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--lyra-color-warning-bg)', color: '#C2620C', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Crown size={17} /></div>
            <div><div style={{ fontSize: 19, fontWeight: 700, color: T.ink }}>{myCount}</div><div style={{ fontSize: 11, color: T.ink4 }}>My spaces</div></div>
          </div>
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--app-primary-soft)', color: 'var(--lyra-color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><User size={17} /></div>
            <div><div style={{ fontSize: 19, fontWeight: 700, color: T.ink }}>{lyrans.length}</div><div style={{ fontSize: 11, color: T.ink4 }}>Lyrans</div></div>
          </div>
        </div>

        {/* ── Tabs + search — underline-tab style, matches the AI Team page ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 20, flexWrap: 'wrap', borderBottom: `1px solid ${T.border}`, paddingBottom: 10 }}>
          <div style={{ display: 'flex', gap: 18 }}>
            {[
              { id: 'all', label: 'All spaces' },
              { id: 'mine', label: 'My spaces' },
              { id: 'lyrans', label: 'Lyrans' },
            ].map(({ id, label }) => {
              const isActive = tab === id;
              return (
                <button key={id} onClick={() => setTab(id)}
                  style={{ border: 'none', background: 'none', padding: '4px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: isActive ? T.accent : T.ink3 }}>
                  {label}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 9, padding: '7px 12px', width: 240 }}>
            <Search size={13} color={T.ink4} />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search spaces or Lyrans…"
              style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 12.5, color: T.ink, width: '100%', fontFamily: 'inherit' }}
            />
          </div>
        </div>

        {/* ── Content ─────────────────────────────────────────────────── */}
        <div>
          {/* My spaces section */}
          {tab === 'all' && (
            <>
              <Section label="My spaces" sub="spaces you own or joined" icon={<Crown size={14} />}>
                {(ownedSpaces.length === 0 && memberSpaces.length === 0) ? (
                  <Empty msg="You don't have any spaces yet — create one or join a space to get started." />
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                    gap: 10,
                  }}>
                    {ownedSpaces.map(s => (
                      <OwnedSpaceCard
                        key={s._id}
                        space={s}
                        onInvite={() => { setInviteSpace(s); setInviteOpen(true); }}
                        onSettings={() => toast('Settings coming soon')}
                        onDelete={() => doDelete(s)}
                        onVisibilityChange={updated => {
                          setSpaces(prev => prev.map(p => p._id === updated._id ? updated : p));
                          setPublicSpaces(prev =>
                            updated.isPublic
                              ? [updated, ...prev.filter(x => x._id !== updated._id)]
                              : prev.filter(x => x._id !== updated._id));
                        }}
                      />
                    ))}
                    {memberSpaces.map(s => (
                      <DiscoverCard
                        key={s._id}
                        space={s}
                        onJoin={() => doJoin(s)}
                        onRequest={() => doRequest(s)}
                        onOpen={() => (window.location.href = `/spaces/${s._id}`)}
                        onLeave={() => doLeave(s)}
                      />
                    ))}
                  </div>
                )}
              </Section>

              {/* Divider */}
              <div style={{ height: 0.5, background: T.border, margin: '4px 0 24px' }} />

              {/* Discover section */}
              <Section label="Discover spaces" sub="public spaces & invitations" icon={<Globe size={14} />}>
                {discoverList.length === 0 ? (
                  <Empty msg="No public spaces available right now." />
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                    gap: 10,
                  }}>
                    {discoverList.map(s => (
                      <DiscoverCard
                        key={s._id}
                        space={s}
                        onJoin={() => doJoin(s)}
                        onRequest={() => doRequest(s)}
                        onOpen={() => (window.location.href = `/spaces/${s._id}`)}
                        onLeave={() => doLeave(s)}
                      />
                    ))}
                  </div>
                )}
              </Section>
            </>
          )}

          {tab === 'mine' && (
            <Section label="My spaces" sub="spaces you own or joined" icon={<Crown size={14} />}>
              {(ownedSpaces.length === 0 && memberSpaces.length === 0) ? (
                <Empty msg="You don't have any spaces yet — create one or join a space to get started." />
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: 10,
                }}>
                  {ownedSpaces.map(s => (
                    <OwnedSpaceCard
                      key={s._id}
                      space={s}
                      onInvite={() => { setInviteSpace(s); setInviteOpen(true); }}
                      onSettings={() => toast('Settings coming soon')}
                      onDelete={() => doDelete(s)}
                      onVisibilityChange={updated => {
                        setSpaces(prev => prev.map(p => p._id === updated._id ? updated : p));
                        setPublicSpaces(prev =>
                          updated.isPublic
                            ? [updated, ...prev.filter(x => x._id !== updated._id)]
                            : prev.filter(x => x._id !== updated._id));
                      }}
                    />
                  ))}
                  {memberSpaces.map(s => (
                    <DiscoverCard
                      key={s._id}
                      space={s}
                      onJoin={() => doJoin(s)}
                      onRequest={() => doRequest(s)}
                      onOpen={() => (window.location.href = `/spaces/${s._id}`)}
                      onLeave={() => doLeave(s)}
                    />
                  ))}
                </div>
              )}
            </Section>
          )}

          {/* Lyrans tab */}
          {tab === 'lyrans' && (
            <Section label="Lyrans directory" sub={`${lyrans.length} registered`} icon={<User size={14} />}>
              {filteredLyrans.length === 0 ? (
                <Empty msg="No Lyrans found." />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {filteredLyrans.map(l => <LyranRow key={l._id} lyran={l} />)}
                </div>
              )}
            </Section>
          )}
        </div>
      </div>

      {/* ── Modals ────────────────────────────────────────────────────── */}
      {showNew && (
        <CreateSpaceModal
          onClose={() => setShowNew(false)}
          onCreate={handleCreate}
        />
      )}

      {inviteOpen && inviteSpace && (
        <InviteModal
          space={inviteSpace}
          lyrans={lyrans}
          onClose={() => { setInviteOpen(false); setInviteSpace(null); }}
          onSend={sendInvites}
        />
      )}
    </div>
  );
}
